import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';

class FirebaseNotificationService {
  constructor() {
    this.notificationsCollection = 'notifications';
    this.snapshotsCollection = 'appointment_snapshots';
  }

  /**
   * Save notification to Firebase
   */
  async saveNotification(notificationData, userId) {
    try {
      const notification = {
        ...notificationData,
        userId: userId,
        isRead: false,
        createdAt: serverTimestamp(),
        needsUserAction: this.requiresUserAction(notificationData.type),
        status: 'pending' // pending, accepted, rejected, auto_applied
      };

      const docRef = await addDoc(collection(db, this.notificationsCollection), notification);
      console.log('🔔 Notification saved to Firebase:', docRef.id);
      
      return {
        success: true,
        id: docRef.id,
        notification
      };
    } catch (error) {
      console.error('Error saving notification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get user notifications
   */
  async getUserNotifications(userId, limitCount = 50) {
    try {
      const q = query(
        collection(db, this.notificationsCollection),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const notifications = [];

      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        });
      });

      return notifications;
    } catch (error) {
      console.error('Error getting notifications:', error);
      return [];
    }
  }

  /**
   * Get unread notifications
   */
  async getUnreadNotifications(userId) {
    try {
      const q = query(
        collection(db, this.notificationsCollection),
        where('userId', '==', userId),
        where('isRead', '==', false),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const notifications = [];

      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        });
      });

      return notifications;
    } catch (error) {
      console.error('Error getting unread notifications:', error);
      return [];
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      await updateDoc(doc(db, this.notificationsCollection, notificationId), {
        isRead: true,
        readAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark all notifications as read for user
   */
  async markAllAsRead(userId) {
    try {
      const q = query(
        collection(db, this.notificationsCollection),
        where('userId', '==', userId),
        where('isRead', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const updatePromises = [];

      querySnapshot.forEach((docSnapshot) => {
        updatePromises.push(
          updateDoc(docSnapshot.ref, {
            isRead: true,
            readAt: serverTimestamp()
          })
        );
      });

      await Promise.all(updatePromises);
      
      return { success: true, updated: updatePromises.length };
    } catch (error) {
      console.error('Error marking all as read:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Handle user action on notification
   */
  async handleUserAction(notificationId, action, actionData = {}) {
    try {
      await updateDoc(doc(db, this.notificationsCollection, notificationId), {
        status: action, // 'accepted', 'rejected', 'dismissed'
        actionData: actionData,
        actionTakenAt: serverTimestamp(),
        isRead: true
      });

      return { success: true };
    } catch (error) {
      console.error('Error handling user action:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    try {
      await deleteDoc(doc(db, this.notificationsCollection, notificationId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get notification count
   */
  async getNotificationCount(userId, unreadOnly = false) {
    try {
      let q;
      if (unreadOnly) {
        q = query(
          collection(db, this.notificationsCollection),
          where('userId', '==', userId),
          where('isRead', '==', false)
        );
      } else {
        q = query(
          collection(db, this.notificationsCollection),
          where('userId', '==', userId)
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting notification count:', error);
      return 0;
    }
  }

  /**
   * Listen for real-time notification updates
   */
  subscribeToNotifications(userId, callback) {
    const q = query(
      collection(db, this.notificationsCollection),
      where('userId', '==', userId),
      where('isRead', '==', false),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const notifications = [];
      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        });
      });
      callback(notifications);
    });
  }

  /**
   * Save appointment snapshot for change detection
   */
  async saveAppointmentSnapshot(appointments, userId) {
    try {
      const snapshot = {
        userId: userId,
        appointments: appointments,
        createdAt: serverTimestamp(),
        appointmentCount: appointments.length
      };

      await addDoc(collection(db, this.snapshotsCollection), snapshot);
      return { success: true };
    } catch (error) {
      console.error('Error saving appointment snapshot:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get latest appointment snapshot
   */
  async getLatestSnapshot(userId) {
    try {
      const q = query(
        collection(db, this.snapshotsCollection),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting latest snapshot:', error);
      return null;
    }
  }

  /**
   * Determine if notification requires user action
   */
  requiresUserAction(type) {
    const userActionTypes = [
      'APPOINTMENT_RESCHEDULED',
      'APPOINTMENT_LOCATION_CHANGED', 
      'APPOINTMENT_PROCEDURE_CHANGED',
      'APPOINTMENT_PRACTITIONER_CHANGED'
    ];
    
    return userActionTypes.includes(type);
  }

  /**
   * Clean up old notifications
   */
  async cleanupOldNotifications(userId, daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const q = query(
        collection(db, this.notificationsCollection),
        where('userId', '==', userId),
        where('createdAt', '<', cutoffDate)
      );

      const querySnapshot = await getDocs(q);
      const deletePromises = [];

      querySnapshot.forEach((doc) => {
        deletePromises.push(deleteDoc(doc.ref));
      });

      await Promise.all(deletePromises);

      return { success: true, deleted: deletePromises.length };
    } catch (error) {
      console.error('Error cleaning up old notifications:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new FirebaseNotificationService();
